import styled from '@emotion/styled';
import React, { useState, useRef } from 'react';
import { Box, BlueButton, WhiteButton, Button, RedButton, TransparentButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useLogger, useClickOutside } from '~Hooks';
import useModal from '~ComponentsImproved/Modal/useModal';
import { BookingStatus } from '../../../../../__queries__/globalTypes';
import useI18n from '~Translations';
import MoveTableModal from '../MoveTableModal';
import { DiscountBadge } from '../../sharedStyles';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';
import { useBookings } from '~Providers/BookingsProvider';
import { ModalContent } from '~Styles/modal';
import Modal from '~ComponentsImproved/Modal';
import { formatPhoneNumber } from '../../utils';
import EditBookingTime from './EditBookingTime';
import EditPartySize from './EditPartySize';
import { GetTables_Venue_floorPlans_tables } from '~Scenes/Venue/__queries__';

const Wrapper = styled.div`
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  overflow-y: auto;
  width: 364px;
  border-left: 1px solid var(--colorBlueGray1000);
`;

const InfoSection = styled(Box)`
  background-color: var(--colorBlueGray1200);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Comment = styled.span`
  display: block;
  border-radius: 4px;
  background-color: transparent;
  border: 1px solid var(--colorBlueGray200);
  font-size: 12px;
  color: var(--colorBlueGray200);
  padding: 8px 12px;
  margin-top: 12px;
  max-width: 100%;
  font-weight: 600;
  line-height: 1.4;
`;

const ManuallyAssigned = styled.div`
  background-color: var(--colorUISand);
  border: 1px solid var(--colorUIYellow);
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 12px;
`;

const CancelBooking = styled(Button)`
  background-color: transparent;
  color: var(--colorUIRed);
  text-decoration: underline;
`;

const CancelModalContent = styled(ModalContent)`
  max-width: 500px;
  text-align: center;
`;

interface Props {
  booking: GetBookings_operativeViewer_bookingsForVenueAndDate;
  tableName?: string;
  tables?: GetTables_Venue_floorPlans_tables[];
  onClose: () => void;
  onNavigateToTable: (tableId: string) => void;
  onHighlightBooking?: (bookingId: string) => void;
  onBookingUpdated?: () => void;
}

const BookingDetails: React.FC<Props> = ({
  booking,
  tableName,
  tables,
  onClose,
  onNavigateToTable,
  onHighlightBooking,
  onBookingUpdated,
}) => {
  const logger = useLogger('booking-details');
  const { i18n } = useI18n();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cancelModal = useModal();
  const moveTableModal = useModal();
  const bookings = useBookings();
  const [isProcessing, setIsProcessing] = useState(false);

  // Use click outside hook to close the details panel, but ignore clicks inside modals
  useClickOutside(wrapperRef, onClose, true, ['[data-booking-id]', '[data-cheque-block]']);

  const cancelButton = useButton({
    onClick: () => {
      cancelModal.toggle();
    },
    disabled: isProcessing || booking.status === BookingStatus.CANCELED,
  });

  const moveTableButton = useButton({
    onClick: () => {
      moveTableModal.toggle();
    },
  });

  const goToTableButton = useButton({
    onClick: () => {
      if (booking.tableIds.length > 0) {
        onNavigateToTable(booking.tableIds[0]);
      }
    },
    disabled: !onNavigateToTable || booking.tableIds.length === 0,
  });

  const handleCancelBooking = async () => {
    setIsProcessing(true);

    const success = await bookings.actions.cancel(booking.id, booking.date);

    if (success) {
      // Refresh the list on screen (the provider only refetches today's bookings itself)
      onBookingUpdated?.();
      cancelModal.toggle();
      onClose();
    }

    setIsProcessing(false);
  };

  const confirmCancelBookingButton = useButton({
    onClick: handleCancelBooking,
    disabled: isProcessing,
  });

  const abortCancelBookingButton = useButton({
    onClick: cancelModal.toggle,
    disabled: isProcessing,
  });

  // Format date display - show "Today", "Tomorrow", or formatted date
  const formatDateDisplay = () => {
    const now = new Date();
    // Create date strings in local timezone to avoid UTC conversion issues
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate(),
    ).padStart(2, '0')}`;

    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

    const bookingDate = booking.date;

    if (bookingDate === today) return i18n('Bookings.Today');
    if (bookingDate === tomorrow) return i18n('Bookings.Tomorrow');

    // Parse the date string manually to avoid timezone issues
    const [year, month, day] = booking.date.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Box marginBottom="medium" display="flex" justifyContent="space-between" alignItems="center">
        <Heading level={3} color="var(--colorWhite)">
          {i18n('Bookings.BookingDetails')}
        </Heading>
      </Box>

      {/* Guest Information */}
      <InfoSection>
        <Heading level={4} bottom={12} color="var(--colorWhite)">
          {i18n('Bookings.GuestInformation')}
        </Heading>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.Name')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {booking.guestName || i18n('Bookings.UnknownGuest')}
          </Paragraph>
        </InfoRow>
        {booking.guestPhone && (
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.Phone')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {formatPhoneNumber(booking.guestPhone)}
            </Paragraph>
          </InfoRow>
        )}
        {booking.guestEmail && !booking.guestEmail.includes('ADMIN_BOOKING') && (
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.Email')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)" style={{ wordBreak: 'break-word' }}>
              {booking.guestEmail}
            </Paragraph>
          </InfoRow>
        )}
      </InfoSection>

      {/* Booking Information */}
      <InfoSection>
        <Heading level={4} bottom={12} color="var(--colorWhite)">
          {i18n('Bookings.BookingInformation')}
        </Heading>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.Date')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {formatDateDisplay()}
          </Paragraph>
        </InfoRow>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.Time')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {booking.startTime} - {booking.endTime}
          </Paragraph>
        </InfoRow>
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.PartySize')}
          </Paragraph>
          <Paragraph level={3} color="var(--colorWhite)">
            {booking.partySize} {i18n('Bookings.Guests')}
          </Paragraph>
        </InfoRow>
        {tableName && (
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.Table')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              {tableName}
            </Paragraph>
          </InfoRow>
        )}
        <InfoRow>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('Bookings.Discount')}
          </Paragraph>
          {booking.discountPercent ? (
            <DiscountBadge>
              {booking.discountPercent}
              {i18n('Bookings.PercentOff')}
            </DiscountBadge>
          ) : (
            <Paragraph level={3} color="var(--colorWhite)">
              {i18n('Bookings.NoDiscount')}
            </Paragraph>
          )}
        </InfoRow>
        {booking.isManuallyAssigned && (
          <ManuallyAssigned>
            <Heading level={5} color="var(--colorBlueGray1300)">
              {i18n('Bookings.ManuallyAssignedTable')}
            </Heading>
          </ManuallyAssigned>
        )}
        {booking.comment && <Comment>{booking.comment}</Comment>}
      </InfoSection>

      {/* Associated Cheque Information (if booking has started) */}
      {booking.chequeId && (
        <InfoSection>
          <Heading level={4} bottom={12} color="var(--colorWhite)">
            {i18n('Bookings.ActiveCheque')}
          </Heading>
          <InfoRow>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('Bookings.ChequeID')}
            </Paragraph>
            <Paragraph level={3} color="var(--colorWhite)">
              #{booking.chequeId.slice(0, 6).toUpperCase()}
            </Paragraph>
          </InfoRow>
        </InfoSection>
      )}

      {/* Action Buttons */}
      <Box display="flex" flexDirection="column" gap="small">
        <WhiteButton fluid {...moveTableButton}>
          {i18n('Bookings.MoveToAnotherTable')}
        </WhiteButton>

        <Box display="flex" gap="small">
          <EditBookingTime booking={booking} onUpdated={onBookingUpdated} />
          <EditPartySize booking={booking} onUpdated={onBookingUpdated} />
        </Box>

        <BlueButton fluid {...goToTableButton}>
          {i18n('Bookings.GoToTable')}
        </BlueButton>
        <CancelBooking fluid {...cancelButton}>
          {isProcessing ? i18n('Bookings.Canceling') : i18n('Bookings.CancelBooking')}
        </CancelBooking>
      </Box>

      {/* Confirmation Modal for Cancellation */}
      {
        <Modal isShowing={cancelModal.isShowing} hide={cancelModal.toggle} backdropClose={true}>
          <CancelModalContent>
            <Heading level={3} color="var(--colorBlueGray100)" bottom={24}>
              {i18n('Bookings.CancelConfirmation')}
            </Heading>
            <Box display="flex" gap="small">
              <WhiteButton fluid {...abortCancelBookingButton}>
                {i18n('Bookings.NoKeepBooking')}
              </WhiteButton>
              <RedButton fluid {...confirmCancelBookingButton}>
                {isProcessing ? i18n('Bookings.Canceling') : i18n('Bookings.YesCancelBooking')}
              </RedButton>
            </Box>
          </CancelModalContent>
        </Modal>
      }

      {/* Move Table Modal */}
      {tables && (
        <MoveTableModal
          isShowing={moveTableModal.isShowing}
          toggle={moveTableModal.toggle}
          tables={tables}
          booking={booking}
          onComplete={() => {
            // Refresh the list on screen so the booking shows on its new table
            onBookingUpdated?.();
            onClose();
          }}
          onHighlightBooking={onHighlightBooking}
        />
      )}
    </Wrapper>
  );
};

export default BookingDetails;
